/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-carbon',
	'../app/nls/StringResources',
	'../lib/@waca/core-client/js/core-client/utils/BidiUtil',
	'@ba/ba-graphics/dist/illustrations-js/no-pins_128',
	'@ba/ba-graphics/dist/icons-js/subtract--alt_32'

], function(React, PropTypes, UI_CARBON_TOOLKIT, UI_CARBON, StringResources, BidiUtil, noPinIcon, removeButton) {

	const { List,
		Label,
		TruncatedText,
		FlexItem,
		FlexLayout,
		Button,
		SVGIcon,
		Container } = UI_CARBON_TOOLKIT;
	const { ClickableTile } = UI_CARBON;
	const keyNames = UI_CARBON_TOOLKIT.keyboardUtils.keyNames;

	class PinsListView extends React.Component {
		constructor(props){
			super(props);
		}

		pinMatches(pinWidget) {
			return this._getSearchableFieldValue(pinWidget).toLowerCase().indexOf(this.props.searchText.toLowerCase()) !== -1;
		}

		/**
		 * Builds searchable string for provided pin
		 *
		 * @public
		 *
		 * @param  {object} value - Pin object
		 * @return {string} fields - String that contains widget info
		 */
		_getSearchableFieldValue(value) {
			// concatenate multiple values to search
			const separator = '___';
			let fields = value.displayName + separator + value.sourceName;
			if (value.contentType === 'boardFragment' && value.content.widgets) {
				// if looking at a boardFragment with widgets, use the mapping labels
				value.content.widgets.forEach(widget => {
					fields = fields + separator + this.props.smartNamingSvc.getLocalizedWidgetType(widget);
					widget.mapping && widget.mapping.forEach(mapping => {
						fields = fields + separator + mapping.label;
					});
					// check live widgets
					const items = widget.data &&
							widget.data.dataViews &&
							widget.data.dataViews[0] &&
							widget.data.dataViews[0].dataItems;
					items && items.forEach(item => {
						fields = fields + separator + item.itemLabel;
					});
				});
			}

			return fields;
		}

		renderThumbnail(pin) {
			return (
				<FlexItem className={'pinThumb'}>
					<img src={pin.thumbUri} />
				</FlexItem>);
		}

		renderPinInfo(pinItem) {
			return (
				<FlexItem>
					<Label className={'pinSource'} aria-label={pinItem.sourceName} label={`${pinItem.sourceName}${StringResources.get('listSeparator')} ${pinItem.pinAge}`} />
				</FlexItem>
			);
		}

		renderPinTitle(item) {
			return (
				<FlexItem className={'pinTitle'}>
					<FlexItem className={'pinNameContainer'} >
						<TruncatedText className={'pinName'} aria-label={item.displayName} value={item.displayName} />
					</FlexItem>
					<FlexItem>
						<Button
							tooltipPosition={'left'}
							tooltipAlignment={'end'}
							title={StringResources.get('pinRemove')}
							className={'removePinButton'}
							icon={removeButton.default.id}
							focusable={true}
							onKeyDown={(event) => this.onKeyDownRemove(event, item.id)}
							onClick={(event) => this.props.onPinDeleted(item.id, event)}
							smartNamingSvc={this.props.smartNamingSvc}
							size="small"
							variant="icon" />
					</FlexItem>
				</FlexItem>
			);
		}

		onKeyDownRemove(event, id) {
			if(event.key.toUpperCase() === keyNames.ENTER){
				event.preventDefault();
				event.stopPropagation();
				this.props.onPinDeleted(id, event);
			}
		}

		itemRenderer(index) {
			const item = this._renderedPins[index];
			return (
				<ClickableTile
					handleKeyDown={this.props.onKeyDown.bind(this, item.id)}
					handleClick={this.props.handleClick.bind(this,  item.id)}
					className={'pinTile pinItem'}
					data-index={index} selectable="true"
					data-id={item.id}
					tabIndex={0}
				>
					<FlexLayout
						fullHeight
						direction="column"
						alignItems='flex-start'
						className='pinTileLayout'
						role={'button'}
					>
						{this.renderThumbnail(item)}
						{this.renderPinTitle(item)}
						{this.renderPinInfo(item)}
					</FlexLayout>
				</ClickableTile>
			);
		}

		renderPinsCount(numPins) {
			const formattedCount = BidiUtil.enforceNumericShaping(numPins);
			return (
				<Label className={'pinCount'} label={
					StringResources.get(('pinCount'), {
						smart_count: formattedCount,
						count: formattedCount
					}) }/>
			);
		}

		render(){
			this._renderedPins = this.props.items.filter(this.pinMatches.bind(this));
			const numPins = this._renderedPins.length;
			const style = { 'overflow-y' : 'hidden' };
			if (numPins > 0) {
				return (
					<Container className={'pinsList'}>
						{this.renderPinsCount(numPins)}
						<List
							className={'list'}
							length={numPins}
							itemRenderer={this.itemRenderer.bind(this)}
							selectionMode='single'
							style={style}
						/>
					</Container>
				);
			} else {
				return (
					<FlexLayout
						className='emptyTableContent'
						direction='column'
						alignItems='center'
					>
						<SVGIcon
							iconId={noPinIcon.default.id}
							className='customVisInfoIcon'
							role='img'
							size="xxlarge"
						/>
						<Label className={'noPinsLabel'} label={StringResources.get('NoPinsCollected')} />
					</FlexLayout>
				);
			}
		}
	}

	PinsListView.propTypes = {
		items: PropTypes.array,
		onKeyDown: PropTypes.function,
		onPinDeleted: PropTypes.function,
		searchText: PropTypes.string,
		smartNamingSvc: PropTypes.object,
		handleClick: PropTypes.function
	};

	return PinsListView;
});